import { useState } from 'react';
import { formatDate, formatGel, formatNumber } from '../lib/format';
import { useI18n, type StringKey } from '../lib/i18n';
import { useOrders, useTransactions, useVehicles } from '../lib/queries';
import { Amount, Card, Skeleton } from '../components/ui';

type Tab = 'visits' | 'cashback' | 'points';

const TABS: Array<{ id: Tab; key: StringKey }> = [
  { id: 'visits', key: 'ledger.visits' },
  { id: 'cashback', key: 'ledger.cashback' },
  { id: 'points', key: 'ledger.points' },
];

/**
 * Every value the cashback and points enums can hold. Listing them rather than
 * building the key by hand means a type added to the database fails the build
 * here instead of showing the customer a raw code like PENDING_RELEASE.
 */
const ENTRY_LABELS: Record<string, StringKey> = {
  EARN: 'ledger.EARN',
  REDEEM: 'ledger.REDEEM',
  ADJUSTMENT: 'ledger.ADJUSTMENT',
  REVERSAL: 'ledger.REVERSAL',
  REFUND_RETURN: 'ledger.REFUND_RETURN',
  PENDING_RELEASE: 'ledger.PENDING_RELEASE',
  EXPIRATION: 'ledger.EXPIRATION',
  MERGE_TRANSFER: 'ledger.MERGE_TRANSFER',
};

function entryLabel(t: (key: StringKey) => string, type: string): string {
  const key = ENTRY_LABELS[type];
  return key ? t(key) : type;
}

function Empty({ text }: { text: string }) {
  return (
    <Card className="py-10 text-center">
      <p className="text-sm text-ink-dim">{text}</p>
    </Card>
  );
}

export function HistoryPage() {
  const { t, language } = useI18n();
  const [tab, setTab] = useState<Tab>('visits');

  const { data, isPending } = useOrders();
  const { data: vehicles } = useVehicles();
  const { data: ledger, isPending: ledgerPending } = useTransactions();

  const items = data?.items ?? [];
  const cashback = ledger?.cashback ?? [];
  const points = ledger?.points ?? [];

  return (
    <div>
      <h1 className="mb-4 font-display text-2xl font-bold tracking-tight text-ink">
        {t('history.title')}
      </h1>

      <div
        role="tablist"
        className="mb-4 grid grid-cols-3 gap-1 rounded-2xl border border-line bg-surface p-1"
      >
        {TABS.map((entry) => (
          <button
            key={entry.id}
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={`min-h-11 rounded-xl px-2 text-xs font-semibold transition-colors ${
              tab === entry.id ? 'bg-raised text-ink' : 'text-ink-faint hover:text-ink-dim'
            }`}
          >
            {t(entry.key)}
          </button>
        ))}
      </div>

      {tab === 'visits' && (
        <>
          {isPending && (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}
            </div>
          )}

          {!isPending && items.length === 0 && <Empty text={t('history.empty')} />}

          <div className="space-y-2">
            {items.map((order) => {
              const vehicle = vehicles?.items.find((v) => v.id === order.vehicleId);
              const refunded = order.status === 'REFUNDED' || order.status === 'PARTIALLY_REFUNDED';
              return (
                <Card key={order.reference} className={refunded ? 'opacity-60' : ''}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="tabular text-sm font-semibold text-ink">{order.reference}</span>
                        {refunded && (
                          <span className="rounded border border-line px-1.5 py-0.5 text-[0.55rem] tracking-wider text-ink-faint">
                            {t('history.refunded')}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[0.7rem] text-ink-faint">
                        {order.completedAt ? formatDate(order.completedAt, language) : ''}
                        {vehicle && ` · ${vehicle.make} ${vehicle.model}`}
                      </div>
                    </div>
                    <Amount
                      value={formatGel(order.orderValueTetri, language)}
                      className="shrink-0 text-base font-semibold text-ink"
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-line pt-2.5 text-[0.7rem]">
                    {order.cashbackUsedTetri > 0 && (
                      <span className="text-brand">
                        −{formatGel(order.cashbackUsedTetri, language)} {t('history.used')}
                      </span>
                    )}
                    <span className="text-positive">
                      +{formatGel(order.cashbackEarnedTetri, language)} {t('history.earned')}
                    </span>
                    <span className="text-ink-faint">
                      +{formatNumber(order.pointsEarned, language)} {t('card.points').toLowerCase()}
                    </span>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {tab === 'cashback' && (
        <>
          {ledgerPending && <Skeleton className="h-40" />}
          {!ledgerPending && cashback.length === 0 && <Empty text={t('ledger.empty')} />}
          {cashback.length > 0 && (
            <Card className="p-0">
              <ul className="divide-y divide-line">
                {cashback.map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm text-ink">{entryLabel(t, entry.type)}</div>
                      <div className="mt-0.5 truncate text-[0.68rem] text-ink-faint">
                        {entry.description ?? formatDate(entry.createdAt, language)}
                      </div>
                    </div>
                    <Amount
                      value={formatGel(Math.abs(entry.amountTetri), language)}
                      className={`shrink-0 text-sm font-semibold ${
                        entry.amountTetri < 0 ? 'text-brand' : 'text-positive'
                      }`}
                    />
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}

      {tab === 'points' && (
        <>
          {ledgerPending && <Skeleton className="h-40" />}
          {!ledgerPending && points.length === 0 && <Empty text={t('ledger.empty')} />}
          {points.length > 0 && (
            <Card className="p-0">
              <ul className="divide-y divide-line">
                {points.map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm text-ink">{entryLabel(t, entry.type)}</div>
                      <div className="mt-0.5 truncate text-[0.68rem] text-ink-faint">
                        {entry.description ?? formatDate(entry.createdAt, language)}
                      </div>
                    </div>
                    <span
                      className={`tabular shrink-0 text-sm font-semibold ${
                        entry.amount < 0 ? 'text-brand' : 'text-positive'
                      }`}
                    >
                      {entry.amount < 0 ? '−' : '+'}{formatNumber(Math.abs(entry.amount), language)}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
